import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  IconButton,
  useTheme,
  useMediaQuery,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore,
  Close,
  Security,
  Person,
  AccountBalance,
  Email,
  Phone,
  Lock,
} from '@mui/icons-material';
import api from '../../utils/api';
import ToastNotification from '../../utils/toastNotification';

interface AddAgentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  birthday: string;
  commission: string;
  isActive: boolean;
  permissions: string[];
}

const permissions = {
  PROPERTY: {
    CREATE: 'property:create',
    READ_ALL: 'property:read_all',
    READ_OWN: 'property:read_own',
    UPDATE: 'property:update',
    UPDATE_OWN: 'property:update_own',
    DELETE: 'property:delete',
    DELETE_OWN: 'property:delete_own',
    APPROVE: 'property:approve',
  },
  CLIENT: {
    READ_ALL: 'client:read_all',
    READ_OWN: 'client:read_own',
    CREATE: 'client:create',
    UPDATE: 'client:update',
    UPDATE_OWN: 'client:update_own',
    DELETE: 'client:delete',
    DELETE_OWN: 'client:delete_own',
  },
  PROJECT: {
    READ_ALL: 'project:read_all',
    CREATE: 'project:create',
    UPDATE: 'project:update',
    DELETE: 'project:delete',
  },
  ACTIVITY: {
    READ_ALL: 'activity:read_all',
  },
};

const defaultAgentPermissions = [
  'property:create',
  'property:read_own',
  'property:update_own',
  'property:delete_own',
  'client:read_own',
  'client:create',
  'client:update_own',
  'client:delete_own',
];

const AddAgentDialog: React.FC<AddAgentDialogProps> = ({ open, onClose, onSuccess }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    birthday: '',
    commission: '5',
    isActive: true,
    permissions: [...defaultAgentPermissions],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const handleChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handlePermissionToggle = (permission: string) => {
    setFormData((prev) => {
      const hasPermission = prev.permissions.includes(permission);
      return {
        ...prev,
        permissions: hasPermission
          ? prev.permissions.filter((p) => p !== permission)
          : [...prev.permissions, permission],
      };
    });
  };

  const handleCategoryToggle = (categoryPermissions: string[]) => {
    const allSelected = categoryPermissions.every((p) =>
      formData.permissions.includes(p)
    );
    setFormData((prev) => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter((p) => !categoryPermissions.includes(p))
        : [...new Set([...prev.permissions, ...categoryPermissions])],
    }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    }

    const commission = parseFloat(formData.commission);
    if (isNaN(commission) || commission < 0 || commission > 100) {
      newErrors.commission = 'Commission must be between 0 and 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      ToastNotification.warning('Please fill in all required fields correctly');
      return;
    }

    setLoading(true);

    try {
      // First create the user/agent account
      const response = await api.post('/auth/register', {
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        birthday: formData.birthday || undefined,
        role: 'ADMIN', // Agents have ADMIN role
      });

      const userId = response.data.user.id;

      // Update user permissions
      await api.put(`/users/${userId}`, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        isActive: formData.isActive,
        permissions: formData.permissions,
      });

      // Get the agent profile and update commission
      const agentResponse = await api.get('/agents');
      const newAgent = agentResponse.data.find(
        (agent: any) => agent.userId === userId
      );

      if (newAgent) {
        await api.put(`/agents/${newAgent.id}`, {
          commission: parseFloat(formData.commission),
          rating: 0,
        });
      }

      ToastNotification.success('Agent created successfully!');
      onSuccess();
      handleClose();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to create agent';
      ToastNotification.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      phone: '',
      birthday: '',
      commission: '5',
      isActive: true,
      permissions: [...defaultAgentPermissions],
    });
    setErrors({});
    onClose();
  };

  const renderPermissionCategory = (
    title: string,
    categoryPerms: Record<string, string>,
    icon: React.ReactNode
  ) => {
    const permissionList = Object.values(categoryPerms);
    const selectedCount = permissionList.filter((p) =>
      formData.permissions.includes(p)
    ).length;
    const allSelected = selectedCount === permissionList.length;

    return (
      <Accordion
        sx={{
          boxShadow: 'none',
          '&:before': { display: 'none' },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px !important',
          mb: 1,
          '&.Mui-expanded': {
            margin: '0 0 8px 0',
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            '&.Mui-expanded': {
              minHeight: 48,
            },
            '& .MuiAccordionSummary-content.Mui-expanded': {
              margin: '12px 0',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            {icon}
            <Typography fontWeight={600}>{title}</Typography>
            <Chip
              label={`${selectedCount}/${permissionList.length}`}
              size="small"
              color={selectedCount > 0 ? 'primary' : 'default'}
              sx={{ ml: 'auto', mr: 1 }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <FormControlLabel
            control={
              <Checkbox
                checked={allSelected}
                indeterminate={selectedCount > 0 && !allSelected}
                onChange={() => handleCategoryToggle(permissionList)}
              />
            }
            label={
              <Typography fontWeight={600} color="primary">
                Select All
              </Typography>
            }
            sx={{ mb: 1 }}
          />
          <Grid container spacing={1}>
            {Object.entries(categoryPerms).map(([key, value]) => (
              <Grid item xs={12} sm={6} key={value}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.permissions.includes(value)}
                      onChange={() => handlePermissionToggle(value)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {key.replace(/_/g, ' ').toLowerCase()}
                    </Typography>
                  }
                  sx={{
                    ml: 2,
                    '& .MuiFormControlLabel-label': {
                      textTransform: 'capitalize',
                    },
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100%' : '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Add New Agent
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" disabled={loading}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, px: isMobile ? 2 : 3 }}>
        {/* Personal Information Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
            📋 Personal Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={handleChange('firstName')}
                error={!!errors.firstName}
                helperText={errors.firstName}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={handleChange('lastName')}
                error={!!errors.lastName}
                helperText={errors.lastName}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                error={!!errors.email}
                helperText={errors.email}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={handleChange('phone')}
                error={!!errors.phone}
                helperText={errors.phone}
                required
                placeholder="+961 XX XXX XXX"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                value={formData.birthday}
                onChange={handleChange('birthday')}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: new Date().toISOString().split('T')[0] }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={formData.password}
                onChange={handleChange('password')}
                error={!!errors.password}
                helperText={errors.password || 'Minimum 6 characters'}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Agent Settings Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
            💼 Agent Settings
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Commission Rate (%)"
                type="number"
                value={formData.commission}
                onChange={handleChange('commission')}
                error={!!errors.commission}
                helperText={errors.commission || 'Agent commission percentage'}
                required
                inputProps={{ min: 0, max: 100, step: 0.5 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccountBalance fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Account Status</InputLabel>
                <Select
                  value={formData.isActive ? 'active' : 'inactive'}
                  label="Account Status"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      isActive: e.target.value === 'active',
                    }))
                  }
                >
                  <MenuItem value="active">
                    <Chip label="Active" color="success" size="small" sx={{ mr: 1 }} />
                    Active
                  </MenuItem>
                  <MenuItem value="inactive">
                    <Chip label="Inactive" color="default" size="small" sx={{ mr: 1 }} />
                    Inactive
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {/* Permissions Section */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Security color="primary" />
            <Typography variant="subtitle1" fontWeight={600}>
              Access Permissions
            </Typography>
            <Chip
              label={`${formData.permissions.length} selected`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Configure what this agent can access and manage in the system
          </Alert>
          {renderPermissionCategory(
            'Property Management',
            permissions.PROPERTY,
            <AccountBalance fontSize="small" color="primary" />
          )}
          {renderPermissionCategory(
            'Client Management',
            permissions.CLIENT,
            <Person fontSize="small" color="primary" />
          )}
          {renderPermissionCategory(
            'Project Access',
            permissions.PROJECT,
            <AccountBalance fontSize="small" color="primary" />
          )}
          {renderPermissionCategory(
            'Activity Logs',
            permissions.ACTIVITY,
            <Security fontSize="small" color="primary" />
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: isMobile ? 2 : 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          gap: 1,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <Button
          onClick={handleClose}
          disabled={loading}
          fullWidth={isMobile}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          fullWidth={isMobile}
          startIcon={loading ? <CircularProgress size={20} /> : <Person />}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            px: 4,
          }}
        >
          {loading ? 'Creating Agent...' : 'Create Agent'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAgentDialog;
